import React from 'react';
import { useAudio } from '../../contexts/AudioContext';
import { Play, Pause, Volume2, VolumeX, Volume1, Loader2 } from 'lucide-react';

export function Controls({ size = 'large' }) {
  const { 
    isPlaying, 
    volume, 
    isMuted, 
    isLoading, 
    togglePlay, 
    changeVolume, 
    toggleMute 
  } = useAudio();

  const VolumeIcon = isMuted || volume === 0 ? VolumeX : volume < 0.5 ? Volume1 : Volume2;

  const buttonSizes = {
    small: 'w-12 h-12',
    medium: 'w-16 h-16',
    large: 'w-20 h-20 md:w-24 md:h-24'
  };

  const iconSizes = {
    small: 'w-5 h-5',
    medium: 'w-6 h-6',
    large: 'w-8 h-8 md:w-10 md:h-10'
  };

  const handleVolumeChange = (e) => {
    const newVol = parseInt(e.target.value) / 100;
    changeVolume(newVol);
  };

  const volumePercent = Math.round(volume * 100);

  return (
    <div className="flex flex-col items-center gap-6" data-testid="player-controls">
      {/* Main play/pause button */}
      <div className="relative">
        {isPlaying && (
          <div 
            className="absolute inset-0 rounded-full animate-ping"
            style={{ 
              backgroundColor: 'var(--primary)',
              opacity: 0.2,
              animationDuration: '2s'
            }}
          />
        )}
        
        <button
          onClick={togglePlay}
          disabled={isLoading}
          className={`relative ${buttonSizes[size]} rounded-full flex items-center justify-center transition-all duration-300 transform hover:scale-105 active:scale-95 disabled:opacity-50`}
          style={{
            background: `linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)`,
            boxShadow: isPlaying 
              ? '0 0 40px rgba(139, 92, 246, 0.5)' 
              : '0 10px 30px rgba(0, 0, 0, 0.3)'
          }}
          data-testid="play-pause-button"
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isLoading ? (
            <Loader2 className={`${iconSizes[size]} text-white animate-spin`} />
          ) : isPlaying ? (
            <Pause className={`${iconSizes[size]} text-white`} fill="white" />
          ) : (
            <Play className={`${iconSizes[size]} text-white ml-1`} fill="white" />
          )}
        </button>
      </div>

      {/* Volume control - Simple and visible */}
      <div className="flex items-center gap-3 w-full max-w-xs" data-testid="volume-controls">
        <button
          onClick={toggleMute}
          className="p-2 flex-shrink-0"
          style={{ color: 'var(--text-muted)' }}
          data-testid="mute-button"
        >
          <VolumeIcon className="w-5 h-5" />
        </button>
        
        <input
          type="range"
          min="0"
          max="100"
          value={volumePercent}
          onChange={handleVolumeChange}
          className="volume-slider flex-1"
          data-testid="volume-slider"
          style={{
            width: '100%',
            height: '8px',
            borderRadius: '4px',
            background: `linear-gradient(to right, #8B5CF6 0%, #22C55E ${volumePercent}%, rgba(255,255,255,0.2) ${volumePercent}%, rgba(255,255,255,0.2) 100%)`,
            WebkitAppearance: 'none',
            appearance: 'none',
            cursor: 'pointer'
          }}
        />
        
        <span 
          className="text-sm font-bold w-10 text-right flex-shrink-0"
          style={{ color: 'var(--text-main)' }}
        >
          {volumePercent}%
        </span>
      </div>
    </div>
  );
}
